import PropTypes from "prop-types"
import { Button, Typography, Box, IconButton, Tooltip, Autocomplete, TextField, CircularProgress, Snackbar, Alert, Badge, Popover } from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import { rootStore } from "stores/root-store"
import { useEffect, useState } from "react"

import { storageName } from "shared/constants/storage-constant"
import * as StorageHelper from "utils/storage-helper"

import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import PaperComponent from "components/common/paper-component"
import { MainNavbarRoot } from "./styles"
import { observer } from "mobx-react-lite"
import NotificationsIcon from "@mui/icons-material/Notifications"
import BusinessIcon from "@mui/icons-material/Business"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { Close } from "@mui/icons-material"
import { TOPBAR_SLOT_ID, TOPBAR_TITLE_SLOT_ID } from "./topbar-portal"
import { setUserContext, trackEvent } from "utils/analytics"

export const MainNavbar = observer(props => {
  const { onSidebarOpen, ...other } = props

  const [isLoading, setIsLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [company, setCompany] = useState("")
  const [selectedCompany, setSelectedCompany] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)

  useEffect(() => {
    const activeCompany = StorageHelper.getStorage(storageName.activeCompany)
    if (activeCompany != "Internal User") {
      setCompany(activeCompany)
      setSelectedCompany(activeCompany)
    }
  }, [])

  const onApply = async () => {
    setIsLoading(true)
    StorageHelper.setStorage(storageName.activeCompany, selectedCompany)
    StorageHelper.deleteStorage(storageName.productList)
    StorageHelper.deleteStorage(storageName.filterCriteria)

    await Promise.all([rootStore.loadProductList(), rootStore.loadAllFilterCriteria(), rootStore.loadCarrierService(), rootStore.loadUserConfigs()])

    setCompany(selectedCompany)
    rootStore.setCompany(selectedCompany)
    // client_code follows the company an internal user is looking at; user_type stays internal
    setUserContext({ companyCode: selectedCompany, isInternal: true })
    trackEvent("company_switch")

    setIsLoading(false)
    setOpen(false)
  }

  const onClose = (ev, reason) => {
    if (reason == "backdropClick" && isLoading) return
    setOpen(false)
  }

  const handleClick = event => {
    // there's no read/unread state yet, so this is the total shown on the badge
    trackEvent("notification_open", { unread_count: rootStore.notifications?.length || 0 })
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const initials = (rootStore.userName || "")
    .split(" ")
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  const chipSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    height: 38,
    border: "1px solid #E6E8F0",
    borderRadius: 999,
    px: 1.25,
    bgcolor: "#fff",
    color: "#12131A",
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: "nowrap",
  }

  return (
    <>
      <MainNavbarRoot
        sx={{
          left: {
            lg: 248,
          },
          width: {
            lg: "calc(100% - 248px)",
          },
        }}
        {...other}
      >
        {rootStore.notifications?.length > 0 && rootStore.openNotification && (
          <Snackbar open={true} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
            <Alert
              severity="warning"
              variant="filled"
              sx={{ width: "100%", maxHeight: 80 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    rootStore.setOpenNotification(false)
                  }}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              <div dangerouslySetInnerHTML={{ __html: rootStore.notificaton }} />
            </Alert>
          </Snackbar>
        )}

        <Box
          sx={{
            mt: 1,
            mb: 1,
            mx: { xs: 1, lg: 2 },
            minHeight: 56,
            bgcolor: "#fff",
            border: "1px solid #E6E8F0",
            borderRadius: 3,
            boxShadow: "0 10px 26px -20px rgba(18,19,26,.35)",
            display: "flex",
            alignItems: "center",
            px: 2,
            gap: 1.5,
          }}
        >
          <IconButton onClick={onSidebarOpen} id="btnBurgerMenu" sx={{ display: { xs: "inline-flex", lg: "none" } }}>
            <MenuIcon fontSize="small" />
          </IconButton>

          {rootStore.screenTitle && (
            <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.01em", color: "#12131A", display: { xs: "none", lg: "block" } }}>{rootStore.screenTitle}</Typography>
          )}

          {/* Page title / greeting portals in here (Home, Outbound, Inbound, …).
              Hidden only on true mobile widths (burger-menu territory) — from
              md up it shows and simply truncates when the bar gets crowded. */}
          <Box
            id={TOPBAR_TITLE_SLOT_ID}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              minWidth: 0,
              flexShrink: 1,
              overflow: "hidden",
              pr: { xs: 0, md: 1 },
            }}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }} />

          {/* Page-specific controls (period filter, exports, …) portal in here */}
          <Box
            id={TOPBAR_SLOT_ID}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              flexShrink: 1,
              overflowX: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          />

          <IconButton onClick={handleClick} sx={{ color: "#4A4F5C", flexShrink: 0 }}>
            <Badge badgeContent={rootStore.notifications?.length || 0} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
            {rootStore.notifications?.length > 0 ? (
              rootStore.notifications.map(x => (
                <Typography key={x.Id} sx={{ p: 2 }}>
                  <div dangerouslySetInnerHTML={{ __html: x.Notification_Content.replaceAll("\\n", "<br/>") }} />
                </Typography>
              ))
            ) : (
              <Typography sx={{ p: 2, color: "#65748B", fontSize: 13 }}>No notifications</Typography>
            )}
          </Popover>

          {rootStore.isSuperUser ? (
            <Tooltip placement="bottom" title="Switch customer">
              <Box id="btnSelectCompany" onClick={() => setOpen(true)} sx={{ ...chipSx, cursor: "pointer", flexShrink: 0, "&:hover": { borderColor: "#C9CDD6" } }}>
                <BusinessIcon sx={{ fontSize: 17, color: "#65748B" }} />
                {company || "Select customer"}
                <KeyboardArrowDownIcon sx={{ fontSize: 17, color: "#65748B" }} />
              </Box>
            </Tooltip>
          ) : (
            company && (
              <Box sx={{ ...chipSx, flexShrink: 0 }}>
                <BusinessIcon sx={{ fontSize: 17, color: "#65748B" }} />
                {company}
              </Box>
            )
          )}

          <Box sx={{ ...chipSx, pl: 0.5, gap: 1, flexShrink: 0 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "#F3DA00", color: "#12131A", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 12 }}>{initials}</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, display: { xs: "none", md: "block" } }}>{rootStore.userName}</Typography>
          </Box>
        </Box>

        <Dialog open={open} onClose={onClose} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title">
          <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
            Select customer
          </DialogTitle>
          <DialogContent dividers>
            <Autocomplete
              id="cboCompanies"
              disableClearable
              fullWidth
              sx={{ minWidth: 300 }}
              value={selectedCompany}
              onChange={(event, item) => setSelectedCompany(item.id)}
              options={rootStore.companyList.map(option => ({ label: `${option.company} - ${option.name}`, id: option.company }))}
              renderInput={params => <TextField {...params} label="Company" />}
            />
          </DialogContent>
          <DialogActions>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Button id="btnApply" onClick={onApply} variant="contained">
                Apply
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Snackbar open={!Boolean(company)} anchorOrigin={{ vertical: "top", horizontal: "right" }} sx={{ marginTop: 3 }}>
          <Alert severity="warning" variant="filled" sx={{ width: "100%" }}>
            Please choose a customer to work with!
          </Alert>
        </Snackbar>
      </MainNavbarRoot>
    </>
  )
})

MainNavbar.propTypes = {
  onSidebarOpen: PropTypes.func,
}
